import React, { useState, useContext } from 'react';
import { useTheme } from '../../contexts/ThemeContext'; // Para obter o tema atual
import type { MarketFilters } from '../../types';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';

// Remover imports do MUI:
// import {
//   Box,
//   Button as MuiButton, // Renomear para evitar conflito, se necessário depois
//   TextField,
//   FormControl,
//   Radio,
//   RadioGroup as MuiRadioGroup,
//   FormControlLabel,
//   Slider as MuiSlider,
//   Typography,
//   Paper,
//   Divider,
//   FormLabel,
//   FormGroup,
//   Checkbox as MuiCheckbox,
//   SelectChangeEvent
// } from '@mui/material';

// Interface de props para o FilterForm
export interface FilterFormProps {
  filters: MarketFilters;
  onFilterChange: (filters: MarketFilters) => void;
}

// Componente para o formulário de filtros
const FilterForm: React.FC<FilterFormProps> = ({ filters, onFilterChange }) => {
  const [localFilters, setLocalFilters] = useState<MarketFilters>(filters);
  const [maxPriceValue, setMaxPriceValue] = useState<number>(filters.max_price || 10000);
  const { theme: currentTheme } = useTheme();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLocalFilters(prev => ({ ...prev, [name]: value }));
  };
  
  const handleRadioChange = (value: string, name: string) => {
    setLocalFilters(prev => ({ ...prev, [name]: value || undefined }));
  };

  const handlePriceChange = (newValue: number[]) => {
    const newMax = newValue[0];
    if (typeof newMax === 'number') {
      setMaxPriceValue(newMax);
      setLocalFilters(prev => ({
        ...prev,
        min_price: undefined,
        max_price: newMax === 10000 ? undefined : newMax,
      }));
    }
  };

  const handleConditionChange = (checked: boolean | 'indeterminate', conditionValue: string) => {
    const condition = conditionValue as 'new' | 'like_new' | 'good' | 'fair' | 'poor';
    setLocalFilters(prev => {
      const currentConditions = prev.condition || [];
      let newConditions: Array<'new' | 'like_new' | 'good' | 'fair' | 'poor'>;

      if (checked) {
        newConditions = [...currentConditions, condition];
      } else {
        newConditions = currentConditions.filter(c => c !== condition);
      }
      
      return {
        ...prev,
        condition: newConditions.length > 0 ? newConditions : undefined
      };
    });
  };

  const handleApplyFilters = () => {
    onFilterChange(localFilters);
  };

  const handleResetFilters = () => {
    const resetFiltersData: MarketFilters = {
      listing_type: undefined,
      min_price: undefined,
      max_price: undefined,
      condition: undefined,
      search_text: '',
      categories: undefined,
      mechanics: undefined,
      min_players: undefined,
      max_players: undefined,
      status: undefined,
      location: undefined,
      min_playtime: undefined,
      max_playtime: undefined,
      min_age: undefined,
      city: undefined,
      state: undefined,
      country: undefined,
      sort_by: undefined,
      order: undefined,
      trade: undefined
    };
    setLocalFilters(resetFiltersData);
    setMaxPriceValue(10000);
    onFilterChange(resetFiltersData);
  };

  return (
    <div className={`p-4 sm:p-6 rounded-xl shadow-lg ${currentTheme === 'dark' ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-800'}`}>
      <h3 className={`text-xl font-semibold mb-4 pb-2 border-b ${currentTheme === 'dark' ? 'border-gray-700 text-blue-400' : 'border-gray-300 text-blue-600'}`}>
        Filtros
      </h3>
      
      <div className="mb-6">
        <Label htmlFor="search_text" className={`block text-sm font-medium mb-1 ${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-700'}`}>
          Buscar
        </Label>
        <Input
          id="search_text"
          name="search_text"
          placeholder="Nome do jogo, categoria..."
          value={localFilters.search_text || ''}
          onChange={handleInputChange}
          className={`w-full ${currentTheme === 'dark' ? 'bg-gray-700 border-gray-600 placeholder-gray-500 text-gray-200 focus:ring-blue-500 focus:border-blue-500' : 'bg-gray-50 border-gray-300 placeholder-gray-400 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
        />
      </div>
      
      <div className="mb-6">
        <Label className={`block text-sm font-medium mb-2 ${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-700'}`}>
          Tipo de Anúncio
        </Label>
        <RadioGroup
          name="listing_type"
          value={localFilters.listing_type || ''}
          onValueChange={(value) => handleRadioChange(value, 'listing_type')}
          className="flex space-x-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="" id="type-all" />
            <Label htmlFor="type-all" className="font-normal">Todos</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="sell" id="type-sell" />
            <Label htmlFor="type-sell" className="font-normal">Venda</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="buy" id="type-buy" />
            <Label htmlFor="type-buy" className="font-normal">Compra</Label>
          </div>
           <div className="flex items-center space-x-2">
            <RadioGroupItem value="trade" id="type-trade" />
            <Label htmlFor="type-trade" className="font-normal">Troca</Label>
          </div>
        </RadioGroup>
      </div>

      {/* Faixa de Preço (MUI Slider para shadcn Slider) */}
      <div className="mb-6">
        <Label className={`block text-sm font-medium mb-2 ${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-700'}`}>
          Faixa de Preço (R$)
        </Label>
        <Slider
          value={[maxPriceValue]}
          onValueChange={handlePriceChange}
          min={0}
          max={10000}
          step={100}
          className={`w-full ${currentTheme === 'dark' ? "[&_[data-slot='track']]:bg-gray-600 [&_[data-slot='range']]:bg-blue-500" : "[&_[data-slot='track']]:bg-gray-200 [&_[data-slot='range']]:bg-blue-600"}`}
        />
        <div className="flex justify-end mt-2 text-xs">
          <span>Máx: R$ {maxPriceValue}</span>
        </div>
      </div>

      {/* Condição do Jogo (MUI Checkbox para shadcn Checkbox) */}
      <div className="mb-6">
        <Label className={`block text-sm font-medium mb-2 ${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-700'}`}>
          Condição do Jogo
        </Label>
        <div className="space-y-2">
          {(['new', 'like_new', 'good', 'fair', 'poor'] as const).map(conditionValue => (
            <div key={conditionValue} className="flex items-center space-x-2">
              <Checkbox
                id={`condition-${conditionValue}`}
                checked={localFilters.condition?.includes(conditionValue) || false}
                onCheckedChange={(checked) => handleConditionChange(checked, conditionValue)}
              />
              <Label htmlFor={`condition-${conditionValue}`} className="font-normal">
                {{
                  'new': 'Novo',
                  'like_new': 'Como Novo',
                  'good': 'Bom',
                  'fair': 'Regular',
                  'poor': 'Ruim'
                }[conditionValue]}
              </Label>
            </div>
          ))}
        </div>
      </div>

      <hr className={`my-6 ${currentTheme === 'dark' ? 'border-gray-700' : 'border-gray-300'}`} />
      
      <div className="flex flex-col sm:flex-row gap-3">
        <Button 
          onClick={handleApplyFilters}
          className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white dark:bg-blue-500 dark:hover:bg-blue-600"
        >
          Aplicar Filtros
        </Button>
        <Button 
          variant="outline"
          onClick={handleResetFilters}
          className="w-full sm:w-auto dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:text-white"
        >
          Limpar Filtros
        </Button>
      </div>
    </div>
  );
};

export default FilterForm; 